/**
 * Sala de chamada embutida na página — usada pelo ATENDENTE e pelo PARCEIRO.
 *
 * **Por que existe (07/08/2026).** Até aqui a chamada de atendimento não
 * existia de verdade. O que havia:
 *
 * - `handleStartCall` no painel do atendente fazia só
 *   `setInCallSince(new Date())` — sem API, sem LiveKit. O atendente via
 *   "Chamada em andamento" com cronômetro correndo e o parceiro nunca ficava
 *   sabendo de nada;
 * - `CallPane` era um ícone estático com um comentário dizendo "PRD 3 vai
 *   plugar `<LiveKitRoom>` aqui";
 * - o portal não tinha interface de chamada nenhuma.
 *
 * **Por que embutida e não em modal.** Um modal que cobre a tela (como a
 * janela de vídeo do chat, `JanelaDeChamada`) não serve aqui: no atendimento os
 * dois lados precisam continuar CONVERSANDO por texto durante a chamada — o
 * próprio histórico marca as mensagens com `during_call`. Uma sala que tapa o
 * chat brigaria com isso.
 *
 * **Um componente para os dois lados.** A alternativa seria uma cópia no painel
 * e outra no portal, que é exatamente o padrão que produziu os defeitos de
 * `[anexo]` e de vídeo nesta mesma tela: cópias divergem, e o lado menos usado
 * é o que fica para trás.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

import { CallControls } from "./CallControls";
import { perfilDeQualidade } from "./qualidadeDeVideo";
import { VideoGrid } from "./VideoGrid";

export interface InlineCallRoomProps {
  /** JWT LiveKit. A sala vai DENTRO dele — não é passada à parte. */
  token: string;
  /** URL WebSocket do LiveKit, como veio do servidor. */
  wsUrl: string;
  /** Chamado quando o usuário encerra pelos controles. */
  onEnd: (reason: "normal" | "forced") => void;
  /** Chamado quando a conexão cai (inclusive quando o outro lado encerra). */
  onDisconnected?: () => void;
  /** Mensagem de erro para a UI de cima mostrar. */
  onErro?: (mensagem: string) => void;
  isRecording?: boolean;
  /** Publica a câmera ao entrar. Padrão `true` (o atendimento entra já com vídeo e não muda).
   *
   *  A ligação de VOZ do chat passa `false`: ela não pode abrir a câmera de ninguém, nem por um
   *  instante. O áudio (microfone) entra ligado nos dois casos. */
  video?: boolean;
  /** Desenha a barra completa (microfone, câmera, tela, encerrar).
   *
   *  `false` na MINIATURA. Ela tem 176px de largura, e a barra tem cinco
   *  botões de 48px — uns 240px. Transbordava e cobria os botões de maximizar
   *  e encerrar da própria miniatura, que ficavam impossíveis de acertar. O
   *  Augusto: *"não consigo clicar direito no botão pra maximizar a chamada
   *  novamente ou pra poder encerrar"*.
   *
   *  E não é só espaço: mexer em microfone e compartilhamento de tela é coisa
   *  de quem está OLHANDO a chamada. Quem minimizou foi fazer outra coisa; ali
   *  só faz sentido voltar ou desligar. */
  mostrarControles?: boolean;
  /** Repassado ao `<VideoGrid>`: na miniatura, só o palco. */
  compacto?: boolean;
  className?: string;
  /** Renderizado DENTRO do contexto da sala.
   *
   *  É o que permite a um filho chamar `useRoomContext()` e publicar mídia —
   *  compartilhamento de tela, por exemplo. Sem isso, quem quisesse publicar
   *  teria de receber a `Room` por prop de fora, e a `Room` só existe depois
   *  que o `<LiveKitRoom>` conecta. */
  children?: ReactNode;
}

export function InlineCallRoom({
  token,
  wsUrl,
  onEnd,
  onDisconnected,
  onErro,
  isRecording = false,
  video = true,
  mostrarControles = true,
  compacto = false,
  className,
  children,
}: InlineCallRoomProps) {
  const [conectado, setConectado] = useState(false);

  /* ------------------------------------------------------------------
   * Identidade ESTÁVEL dos callbacks — o que impede a chamada de cair.
   *
   * Antes, os três eram funções inline (`onConnected={() => setConectado(true)}`).
   * O `useLiveKitRoom` tem essas funções nas dependências do efeito que conecta,
   * então cada renderização do pai fazia o efeito rodar de novo e chamar
   * `room.connect()` e `publishTrack()` outra vez.
   *
   * O pai re-renderizava a cada SEGUNDO: o cronômetro da chamada morava nele
   * (agora está em `DuracaoDaChamada`, isolado). Medido no LiveKit de produção:
   * quinze `already connected to room` e, treze segundos depois,
   * `NegotiationError: negotiation timed out` — a enxurrada de negociações
   * concorrentes quebrava o SDP e o cliente saía sozinho.
   *
   * As refs guardam os callbacks recebidos; os `useCallback` abaixo têm
   * dependências VAZIAS e portanto identidade constante para sempre. Assim um
   * pai que re-renderize por qualquer motivo — e ele vai — não derruba a sala.
   * ------------------------------------------------------------------ */
  const onDisconnectedRef = useRef(onDisconnected);
  onDisconnectedRef.current = onDisconnected;
  const onErroRef = useRef(onErro);
  onErroRef.current = onErro;

  /**
   * Teto de resolução e de taxa de bits, por aparelho.
   *
   * O Augusto relatou movimento travado ao falar do celular com o notebook:
   * *"as câmeras têm uma qualidade muito grande … pesou muito na chamada"*. O
   * custo está em CAPTURAR e CODIFICAR — trabalho do processador de quem envia,
   * pago antes de qualquer byte sair. Ver `qualidadeDeVideo.ts`.
   *
   * ⚠️ `useMemo` com dependência VAZIA não é economia de cálculo: é a mesma
   * exigência dos callbacks acima. `options` está nas dependências do efeito
   * que conecta, então um objeto novo a cada renderização reconectaria a sala —
   * recriando exatamente o defeito que derrubava a chamada aos 13 segundos.
   * O perfil não muda durante a chamada, então calcular uma vez é correto.
   */
  const opcoesDaSala = useMemo(() => {
    const perfil = perfilDeQualidade();
    return {
      videoCaptureDefaults: perfil.captura,
      publishDefaults: perfil.publicacao,
    };
  }, []);

  const aoConectar = useCallback(() => setConectado(true), []);
  const aoDesconectar = useCallback(() => {
    setConectado(false);
    onDisconnectedRef.current?.();
  }, []);
  const aoErrar = useCallback((e: Error) => {
    onErroRef.current?.(e?.message || "Falha ao conectar na chamada.");
  }, []);

  return (
    <div
      className={cn("relative bg-zinc-900 text-zinc-100", className)}
      data-testid="inline-call-room"
    >
      <LiveKitRoom
        token={token}
        serverUrl={wsUrl}
        connect
        // Entra já publicando: quem clicou em chamar (ou em atender) já
        // manifestou a intenção. Pedir para ligar microfone depois de entrar
        // seria a mesma intenção duas vezes — e é como se perde o começo da
        // conversa ("alô? não te ouço").
        audio
        video={video}
        options={opcoesDaSala}
        onConnected={aoConectar}
        onDisconnected={aoDesconectar}
        // Sem isto, uma falha de conexão (token expirado, LiveKit fora do ar,
        // rede bloqueando WebRTC) é silenciosa: a tela fica em "conectando…"
        // para sempre e ninguém sabe por quê.
        onError={aoErrar}
        className="h-full w-full"
        data-lk-theme="default"
      >
        {/* Coluna: vídeo em cima, controles embaixo — DISTRIBUINDO, não
            sobrepondo.
            
            Até 09/08/2026 os controles eram `absolute bottom-6` sobre a área
            de vídeo. No celular a galeria de participantes fica exatamente ali
            embaixo, e a barra a cobria por inteiro: o Augusto não conseguia
            nem ver as miniaturas, quanto mais tocá-las.
            
            Sobreposição nesta tela já causou três defeitos. Distribuir elimina
            a classe inteira: não há empilhamento para acertar quando as duas
            coisas ocupam espaços próprios. */}
        <div className="flex h-full w-full flex-col">
          <div className="min-h-0 flex-1">
            <VideoGrid compacto={compacto} />
          </div>
          {mostrarControles && (
            <div className="shrink-0 border-t border-zinc-800">
              <CallControls onEnd={onEnd} isRecording={isRecording} flutuante={false} />
            </div>
          )}
        </div>
        {/* Renderiza o áudio dos outros participantes. Sem ele há vídeo e
            NENHUM som — e o sintoma é fácil de confundir com microfone do
            outro lado desligado. */}
        <RoomAudioRenderer />

        {children}
      </LiveKitRoom>

      {!conectado && (
        <div
          data-testid="inline-call-conectando"
          className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-zinc-900/80 text-sm"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Conectando…
        </div>
      )}
    </div>
  );
}
